import { type FormEvent, type SVGProps, useEffect, useMemo, useState } from "react"
import { HandleSSOCallback, useClerk, useSignUp as useSignalSignUp } from "@clerk/react"
import { useSignIn, useSignUp as useLegacySignUp } from "@clerk/react/legacy"
import { Loader2 } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "@/components/ui/password-input"
import { capturePlatformEvent } from "services/platformAnalytics"
import {
  beginClerkSelfServiceSignupAttempt,
  clearClerkSelfServiceSignupAttempt,
  completeClerkSelfServiceSignupAttempt,
  recordTransferredSignupIntent,
} from "@/utils/authSessionStorage"

type AuthMode = "sign-up" | "sign-in"
type AuthStep = "email" | "verify"

const HERO_STATS = [
  { value: "1–2", label: "photos to digitize glasses" },
  { value: "Bulk", label: "makeup upload" },
  { value: "∞", label: "products, every plan" },
] as const

const ACCOUNT_NOT_FOUND_MESSAGE =
  "We couldn't find an account for this email. Create an account to continue."
// Shown when the Initializer signs the user out with ?reason=auth-resolution:
// the sign-in succeeded but no provisioned workspace could be resolved for it.
const AUTH_RESOLUTION_MESSAGE =
  "You were signed out because your workspace isn't linked to this account yet. " +
  "Please sign in again — if this keeps happening, contact support so we can finish setting up your account."
const ACCOUNT_EXISTS_MESSAGE = "An account already exists for this email. Please sign in instead."
const ACCOUNT_NOT_FOUND_CODES = ["form_identifier_not_found", "identifier_not_found"] as const
const ACCOUNT_EXISTS_CODES = [
  "form_identifier_exists",
  "identifier_exists",
  "email_address_exists",
] as const
const AUTH_INPUT_CLASS =
  "h-12 rounded-xl border-[#dedde6] bg-white px-4 text-[0.95rem] font-semibold text-[var(--auth-foreground)] shadow-none placeholder:font-normal placeholder:text-[var(--auth-muted)] placeholder:opacity-100 focus-visible:ring-[var(--auth-primary)]"

const getClerkErrorMessage = (error: unknown) => {
  const clerkErrors = (error as { errors?: Array<{ longMessage?: string; message?: string }> })
    ?.errors
  const firstError = clerkErrors?.[0]

  if (firstError?.longMessage) return firstError.longMessage
  if (firstError?.message) return firstError.message
  if (error instanceof Error) return error.message

  return "Unable to complete authentication. Please try again."
}

const hasClerkErrorCode = (error: unknown, codes: readonly string[]) => {
  const clerkErrors = (error as { errors?: Array<{ code?: string }> })?.errors
  return (
    clerkErrors?.some(
      (clerkError) => typeof clerkError.code === "string" && codes.includes(clerkError.code)
    ) ?? false
  )
}

const isAccountNotFoundError = (error: unknown) => hasClerkErrorCode(error, ACCOUNT_NOT_FOUND_CODES)

const isTransferableSignIn = (signIn: {
  firstFactorVerification?: { status?: string | null } | null
}) => signIn.firstFactorVerification?.status === "transferable"

const isAccountNotFoundMessage = (error: string | null) => error === ACCOUNT_NOT_FOUND_MESSAGE

const GoogleIcon = ({ className, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    className={className}
    viewBox="0 0 48 48"
    width="18"
    height="18"
    aria-hidden="true"
    {...props}
  >
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C40.9 36.6 44 31 44 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
)

const AuthHero = () => (
  <section
    className="flex min-h-[42rem] flex-col justify-center overflow-hidden bg-[linear-gradient(135deg,#f4efff_0%,#eee8fb_55%,#f8eef6_100%)] px-6 py-10 sm:px-10 lg:min-h-screen lg:px-12 xl:px-16"
    data-testid="login-hero"
  >
    <div className="mx-auto flex w-full max-w-[41rem] flex-col gap-5">
      <h2 className="auth-display text-[1.35rem] font-extrabold leading-tight text-[var(--auth-foreground)] sm:text-[1.65rem]">
        Built for eyewear and cosmetics brands
      </h2>

      <div className="relative aspect-[1.54/1] overflow-hidden rounded-[1.25rem] bg-[#171021] shadow-[0_26px_60px_-24px_rgba(28,16,70,.5)]">
        <img
          src="/auth/virtual-try-on-hero.jpg"
          alt="Live virtual try-on with makeup and eyewear on one model"
          className="h-full w-full object-cover"
        />
        <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-[#15131f]/60 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
          <span className="size-[0.45rem] rounded-full bg-[#5be08a] shadow-[0_0_0_4px_rgba(91,224,138,.28)]" />
          Live AR try-on
        </span>
        <div className="absolute bottom-4 right-4 flex flex-col items-end gap-2">
          <span className="rounded-full bg-[linear-gradient(135deg,#6e3aff,#b43bd6)] px-4 py-1.5 text-xs font-bold text-white">
            Eyewear
          </span>
          <span className="rounded-full bg-[linear-gradient(135deg,#ff7a59,#ff5e9c)] px-4 py-1.5 text-xs font-bold text-white">
            Makeup
          </span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {HERO_STATS.map((stat) => (
          <Card
            key={stat.label}
            className="border-white/60 bg-white/70 p-4 shadow-none ring-1 ring-white/70"
          >
            <div className="auth-display text-[1.65rem] font-extrabold leading-none text-[var(--auth-primary)]">
              {stat.value}
            </div>
            <div className="mt-1 text-xs font-medium leading-snug text-[var(--auth-muted)]">
              {stat.label}
            </div>
          </Card>
        ))}
      </div>
    </div>
  </section>
)

export const LoginSsoCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signUp: callbackSignUp } = useSignalSignUp()
  // Which form initiated the OAuth round-trip; carried on the redirect URL so
  // it survives the trip to Google and back without depending on storage.
  const startedFromSignIn = searchParams.get("flow") === "sign-in"
  // Snapshot the callback resource before HandleSSOCallback transfers it.
  // Reading the mutable resource after `signIn.create({ transfer: true })`
  // could misclassify an existing account as a newly completed sign-up.
  const callbackSignUpIsTransferable = callbackSignUp.isTransferable
  const callbackSignUpStatus = callbackSignUp.status

  const navigateTo = (to: string) => {
    if (to.startsWith("http")) {
      window.location.href = to
      return
    }

    navigate(to, { replace: true })
  }

  return (
    <div className="auth-page flex min-h-screen items-center justify-center bg-white px-6">
      <div className="flex flex-col items-center gap-3 text-center text-sm text-[var(--auth-muted)]">
        <Loader2 className="size-6 animate-spin text-[var(--auth-primary)]" />
        <p>Completing sign in...</p>
      </div>
      <HandleSSOCallback
        navigateToApp={({ decorateUrl, session }) => {
          // Sign-in with a Google account that has no user yet: Clerk
          // transfers the attempt into a sign-up and CREATES the account
          // during this callback. Without a recorded signup intent that
          // account would never be provisioned — every future sign-in would
          // resolve no workspace and bounce back out. The live (not
          // snapshotted) SignUp resource only completes in a sign-in flow via
          // that transfer, so it is safe evidence of a new account here.
          if (startedFromSignIn && callbackSignUp.status === "complete")
            recordTransferredSignupIntent(session?.user?.id ?? "")
          // Clerk can transfer a sign-up attempt for an existing account into
          // sign-in. Only a completed, non-transferable SignUp resource is
          // evidence that this callback created a new self-service account.
          else
            completeClerkSelfServiceSignupAttempt({
              isTransferable: callbackSignUpIsTransferable,
              status: callbackSignUpStatus,
              subject: session?.user?.id ?? "",
            })
          navigateTo(decorateUrl("/"))
        }}
        navigateToSignIn={() => {
          clearClerkSelfServiceSignupAttempt()
          navigate("/login?mode=sign-in", { replace: true })
        }}
        navigateToSignUp={() => {
          clearClerkSelfServiceSignupAttempt()
          navigate("/login", { replace: true })
        }}
      />
    </div>
  )
}

export const LoginPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const clerk = useClerk()
  const signInState = useSignIn()
  const signUpState = useLegacySignUp()
  const mode: AuthMode = searchParams.get("mode") === "sign-in" ? "sign-in" : "sign-up"
  // Captured once on mount: the Initializer's signOut redirect carries
  // ?reason=auth-resolution; the param is stripped from the URL right away so
  // a refresh doesn't re-show the notice.
  const [showAuthResolutionNotice] = useState(
    () => searchParams.get("reason") === "auth-resolution"
  )
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [submittedEmail, setSubmittedEmail] = useState("")
  const [code, setCode] = useState("")
  const [step, setStep] = useState<AuthStep>("email")
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isClerkReady = signInState.isLoaded && signUpState.isLoaded
  const isSignUp = mode === "sign-up"
  const title = isSignUp ? "Create account" : "Sign in"
  const description = isSignUp
    ? "The easiest way to add virtual try-on to your store. No integration. AI digitization in minutes."
    : "Continue to your virtual try-on workspace with your business email."
  const primaryAction = step === "verify" ? "Verify code" : isSignUp ? "Continue" : "Continue"

  const footer = useMemo(
    () =>
      isSignUp
        ? {
            label: "Already have an account?",
            link: "Sign in",
            mode: "sign-in" as const,
          }
        : {
            label: "Need an account?",
            link: "Create account",
            mode: "sign-up" as const,
          },
    [isSignUp]
  )

  useEffect(() => {
    setStep("email")
    setPassword("")
    setCode("")
    setSubmittedEmail("")
    setError(null)
    setIsSubmitting(false)
  }, [mode])

  // Back from the Google OAuth page restores this page from the bfcache with
  // isSubmitting still true — every control would stay disabled forever.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsSubmitting(false)
    }
    window.addEventListener("pageshow", handlePageShow)
    return () => window.removeEventListener("pageshow", handlePageShow)
  }, [])

  useEffect(() => {
    if (searchParams.get("reason") !== "auth-resolution") return
    const next = new URLSearchParams(searchParams)
    next.delete("reason")
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const showAccountNotFound = () => {
    setStep("email")
    setCode("")
    setError(ACCOUNT_NOT_FOUND_MESSAGE)
  }

  const activateSession = async (sessionId: string | null | undefined) => {
    if (!sessionId) {
      setError("Authentication completed, but no session was returned. Please try again.")
      return false
    }

    await clerk.setActive({ session: sessionId })
    return true
  }

  const completeAuthentication = async (
    sessionId: string | null | undefined,
    options?: { completedSignupSubject?: string | null }
  ) => {
    if (options?.completedSignupSubject)
      completeClerkSelfServiceSignupAttempt({
        isTransferable: false,
        status: "complete",
        subject: options.completedSignupSubject,
      })
    else clearClerkSelfServiceSignupAttempt()

    const activated = await activateSession(sessionId)
    if (!activated) return

    navigate("/", { replace: true })
  }

  const startSignInEmailFlow = async (normalizedEmail: string) => {
    if (!signInState.isLoaded) return

    const signIn = await signInState.signIn.create({
      identifier: normalizedEmail,
      strategy: "email_code",
    })

    setSubmittedEmail(normalizedEmail)

    if (isTransferableSignIn(signIn)) {
      showAccountNotFound()
      return
    }

    if (signIn.status === "complete") {
      await completeAuthentication(signIn.createdSessionId)
      return
    }

    if (signIn.status === "needs_first_factor") {
      setStep("verify")
      return
    }

    if (signIn.status === "needs_second_factor") {
      setError(
        "This account requires an additional sign-in step. Please use your Clerk account portal."
      )
      return
    }

    setError("Unable to continue with this email. Please try another sign-in method.")
  }

  const startSignUpEmailFlow = async (normalizedEmail: string) => {
    if (!signUpState.isLoaded) return

    if (!password.trim()) {
      setError("Create a password to continue.")
      return
    }

    const signUp = await signUpState.signUp.create({
      emailAddress: normalizedEmail,
      password,
    })
    beginClerkSelfServiceSignupAttempt()

    setSubmittedEmail(normalizedEmail)

    if (signUp.status === "complete") {
      await completeAuthentication(signUp.createdSessionId, {
        completedSignupSubject: signUp.createdUserId,
      })
      return
    }

    await signUpState.signUp.prepareEmailAddressVerification({
      strategy: "email_code",
    })
    setStep("verify")
  }

  const startEmailFlow = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSignUp) capturePlatformEvent("registration_button_tapped", { method: "email" })
    if (!signInState.isLoaded || !signUpState.isLoaded) return

    const normalizedEmail = email.trim()
    if (!normalizedEmail) {
      setError("Enter your work email to continue.")
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSubmittedEmail(normalizedEmail)

    try {
      if (isSignUp) await startSignUpEmailFlow(normalizedEmail)
      else await startSignInEmailFlow(normalizedEmail)
    } catch (authError) {
      if (!isSignUp && isAccountNotFoundError(authError)) {
        showAccountNotFound()
        return
      }

      if (isSignUp && hasClerkErrorCode(authError, ACCOUNT_EXISTS_CODES)) {
        setError(ACCOUNT_EXISTS_MESSAGE)
        return
      }

      setError(getClerkErrorMessage(authError))
    } finally {
      setIsSubmitting(false)
    }
  }

  const verifySignInEmailCode = async (normalizedCode: string) => {
    if (!signInState.isLoaded) return

    const signIn = await signInState.signIn.attemptFirstFactor({
      code: normalizedCode,
      strategy: "email_code",
    })

    if (signIn.status === "complete") {
      await completeAuthentication(signIn.createdSessionId)
      return
    }

    if (signIn.status === "needs_second_factor") {
      setError(
        "This account requires an additional sign-in step. Please use your Clerk account portal."
      )
      return
    }

    setError("Verification is not complete yet. Please check the code and try again.")
  }

  const verifySignUpEmailCode = async (normalizedCode: string) => {
    if (!signUpState.isLoaded) return

    const signUp = await signUpState.signUp.attemptEmailAddressVerification({
      code: normalizedCode,
    })

    if (signUp.status === "complete") {
      await completeAuthentication(signUp.createdSessionId, {
        completedSignupSubject: signUp.createdUserId,
      })
      return
    }

    if (signUp.status === "missing_requirements" && signUp.missingFields.includes("password")) {
      setError("Create a password to finish registration.")
      setStep("email")
      return
    }

    setError("Verification is not complete yet. Please check the code and try again.")
  }

  const verifyEmailCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!signInState.isLoaded || !signUpState.isLoaded) return

    const normalizedCode = code.trim()
    if (!normalizedCode) {
      setError("Enter the verification code from your email.")
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (isSignUp) await verifySignUpEmailCode(normalizedCode)
      else await verifySignInEmailCode(normalizedCode)
    } catch (authError) {
      if (isAccountNotFoundError(authError)) {
        showAccountNotFound()
        return
      }

      setError(getClerkErrorMessage(authError))
    } finally {
      setIsSubmitting(false)
    }
  }

  const resendCode = async () => {
    if (!signInState.isLoaded || !signUpState.isLoaded) return

    const emailFactor = signInState.signIn.supportedFirstFactors?.find(
      (factor) => factor.strategy === "email_code"
    )

    setIsSubmitting(true)
    setError(null)

    try {
      if (isSignUp)
        await signUpState.signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        })
      else if (emailFactor?.strategy === "email_code")
        await signInState.signIn.prepareFirstFactor({
          emailAddressId: emailFactor.emailAddressId,
          strategy: "email_code",
        })
      else
        await signInState.signIn.create({
          identifier: submittedEmail || email.trim(),
          strategy: "email_code",
        })
    } catch (authError) {
      if (isAccountNotFoundError(authError)) {
        showAccountNotFound()
        return
      }

      setError(getClerkErrorMessage(authError))
    } finally {
      setIsSubmitting(false)
    }
  }

  const startGoogleFlow = async () => {
    if (isSignUp) capturePlatformEvent("registration_button_tapped", { method: "google" })
    if (!signInState.isLoaded || !signUpState.isLoaded) return

    setIsSubmitting(true)
    setError(null)

    try {
      const params = {
        redirectUrl: `/login/sso-callback?flow=${mode}`,
        redirectUrlComplete: "/",
        strategy: "oauth_google" as const,
      }

      if (isSignUp) {
        beginClerkSelfServiceSignupAttempt()
        await signUpState.signUp.authenticateWithRedirect(params)
      } else {
        clearClerkSelfServiceSignupAttempt()
        await signInState.signIn.authenticateWithRedirect(params)
      }
    } catch (authError) {
      clearClerkSelfServiceSignupAttempt()
      setIsSubmitting(false)
      setError(getClerkErrorMessage(authError))
    }
  }

  const switchMode = () => {
    clearClerkSelfServiceSignupAttempt()
    if (footer.mode === "sign-in") setSearchParams({ mode: "sign-in" })
    else setSearchParams({})
  }

  return (
    <main
      className="auth-page grid min-h-screen bg-white lg:grid-cols-[47%_53%]"
      data-testid="login-page"
    >
      <section className="flex flex-col px-6 py-8 sm:px-10 lg:min-h-screen lg:px-12 xl:px-20">
        <img
          src="/auth/banuba-logo.svg"
          alt="Banuba"
          className="mx-auto h-7 w-auto shrink-0 opacity-95 sm:h-8"
          data-testid="login-logo"
        />

        <div className="mx-auto flex w-full max-w-[19.25rem] flex-1 flex-col justify-center py-10 sm:max-w-[23rem]">
          <Badge className="mb-5 w-fit rounded-full border-transparent bg-[var(--auth-pill)] px-3 py-1.5 text-[0.8rem] font-extrabold text-[var(--auth-primary)] hover:bg-[var(--auth-pill)]">
            Set up in minutes
          </Badge>

          <h1 className="auth-display text-[2rem] font-extrabold leading-tight text-[var(--auth-foreground)] sm:text-[2.45rem]">
            {title}
          </h1>
          <p className="mt-2.5 text-[0.95rem] font-medium leading-relaxed text-[var(--auth-muted)]">
            {description}
          </p>

          {showAuthResolutionNotice ? (
            <div
              className="mt-4 rounded-xl border border-[#f0d4d4] bg-[#fdf2f2] px-4 py-3 text-[0.85rem] font-medium leading-relaxed text-[#8a3535]"
              data-testid="login-auth-resolution-notice"
            >
              {AUTH_RESOLUTION_MESSAGE}
            </div>
          ) : null}

          <form
            className="mt-7 flex flex-col gap-4"
            onSubmit={step === "verify" ? verifyEmailCode : startEmailFlow}
          >
            <FieldGroup className="gap-4">
              {step === "verify" ? (
                <Field data-invalid={Boolean(error)}>
                  <FieldLabel htmlFor="auth-code">Verification code</FieldLabel>
                  <Input
                    id="auth-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder=""
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    disabled={!isClerkReady || isSubmitting}
                    aria-invalid={Boolean(error)}
                    className={AUTH_INPUT_CLASS}
                    data-testid="login-code-input"
                  />
                  <FieldDescription className="text-xs text-[var(--auth-muted)]">
                    We sent a one-time code to {submittedEmail || email}.
                  </FieldDescription>
                </Field>
              ) : (
                <>
                  <Field data-invalid={Boolean(error)}>
                    <FieldLabel htmlFor="auth-email">Work email</FieldLabel>
                    <Input
                      id="auth-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@yourbrand.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      disabled={!isClerkReady || isSubmitting}
                      aria-invalid={Boolean(error)}
                      className={AUTH_INPUT_CLASS}
                      data-testid="login-email-input"
                    />
                    <FieldDescription className="text-xs text-[var(--auth-muted)]">
                      Your business email keeps your store &amp; team together.
                    </FieldDescription>
                  </Field>

                  {isSignUp ? (
                    <Field data-invalid={Boolean(error)}>
                      <FieldLabel htmlFor="auth-password">Password</FieldLabel>
                      <PasswordInput
                        id="auth-password"
                        autoComplete="new-password"
                        placeholder="Create a password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        disabled={!isClerkReady || isSubmitting}
                        aria-invalid={Boolean(error)}
                        className={`${AUTH_INPUT_CLASS} pr-10`}
                        data-testid="login-password-input"
                      />
                    </Field>
                  ) : null}
                </>
              )}

              {error ? <FieldError data-testid="login-error">{error}</FieldError> : null}
              {!isSignUp && isAccountNotFoundMessage(error) ? (
                <FieldDescription
                  className="text-xs text-[var(--auth-muted)]"
                  data-testid="login-create-account-suggestion"
                >
                  Use Create account below to register with email and password.
                </FieldDescription>
              ) : null}
            </FieldGroup>

            <Button
              type="submit"
              disabled={!isClerkReady || isSubmitting}
              className="h-[3.15rem] rounded-xl bg-[var(--auth-primary)] text-[0.98rem] font-extrabold text-white shadow-none hover:bg-[#5f2bf0]"
              data-testid="login-continue"
            >
              {isSubmitting ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
              {primaryAction}
            </Button>

            {step === "verify" ? (
              <Button
                type="button"
                variant="ghost"
                disabled={!isClerkReady || isSubmitting}
                onClick={resendCode}
                className="h-9 text-sm font-bold text-[var(--auth-primary)] hover:bg-[var(--auth-pill)] hover:text-[var(--auth-primary)]"
                data-testid="login-resend-code"
              >
                Resend code
              </Button>
            ) : null}

            <FieldSeparator className="my-0 text-xs text-[var(--auth-muted)]">or</FieldSeparator>

            <Button
              type="button"
              variant="outline"
              disabled={!isClerkReady || isSubmitting}
              onClick={startGoogleFlow}
              className="h-12 rounded-xl border-[#dedde6] bg-white text-[0.9rem] font-extrabold text-[var(--auth-foreground)] shadow-none hover:bg-[#fafafa]"
              data-testid="login-google"
            >
              <GoogleIcon data-icon="inline-start" />
              Continue with Google
            </Button>

            <div id="clerk-captcha" className="auth-clerk-captcha" />
          </form>

          <p className="mt-6 text-sm font-semibold text-[var(--auth-muted)]">
            {footer.label}{" "}
            <button
              type="button"
              onClick={switchMode}
              className="font-extrabold text-[var(--auth-primary)] hover:underline"
              data-testid={footer.mode === "sign-in" ? "login-sign-in" : "login-sign-up"}
            >
              {footer.link}
            </button>
          </p>
        </div>
      </section>

      <AuthHero />
    </main>
  )
}
